import React, { useId, useRef, useState } from 'react';
import styled from 'styled-components';
import { cardSurface } from './ServiceBlocks';

/**
 * A tabbed card that follows the WAI-ARIA tabs pattern: one tab stop for the
 * whole list (roving tabindex), Left/Right/Home/End to move, `aria-selected`
 * and `aria-controls` on every tab, and a focusable panel.
 *
 * SEO: every panel is rendered into the HTML. The inactive ones carry the
 * `hidden` attribute, so a crawler reads all of the text (Google indexes
 * content hidden for UX reasons on a mobile-first page), and nothing is
 * mounted on click or fetched later. Do not change that to conditional
 * rendering.
 */
export interface ServiceTabItem {
  /** Stable key; also part of the element ids. */
  key: string;
  /** One short word: four tabs have to fit a 358px phone. */
  label: string;
  icon?: React.ReactNode;
  panel: React.ReactNode;
}

interface ServiceTabsProps {
  /** Accessible name for the tab list. */
  label: string;
  tabs: ServiceTabItem[];
  /** Called with the tab's key when a visitor selects a different tab (not on first render). */
  onSelect?: (key: string) => void;
}

const Card = styled.div`
  ${cardSurface}
  overflow: hidden;
`;

/* Tabs size to their labels and share the row. If a very narrow phone cannot fit them,
   the row scrolls sideways rather than wrapping a label or pressing the tabs together. */
const TabList = styled.div`
  display: flex;
  gap: 4px;
  padding: 6px;
  overflow-x: auto;
  background: var(--davinci-steam);
  border-bottom: 1px solid var(--davinci-cloud);
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Tab = styled.button`
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  min-height: 46px;
  padding: 0.4rem 0.6rem;
  white-space: nowrap;
  border: 0;
  border-radius: 12px;
  background: transparent;
  color: var(--davinci-midnight);
  font: inherit;
  font-size: var(--fs-sm);
  font-weight: var(--font-weight-bold);
  line-height: 1.2;
  cursor: pointer;
  transition: background-color var(--transition-fast), color var(--transition-fast);

  svg {
    display: none;
    flex: none;
    width: 18px;
    height: 18px;
  }

  &:hover {
    background: var(--white);
  }

  &[aria-selected='true'] {
    background: var(--davinci-midnight);
    color: var(--white);
    box-shadow: 0 4px 12px rgba(20, 53, 98, 0.25);
  }

  &:focus-visible {
    outline: 3px solid var(--davinci-blue-fill);
    outline-offset: 2px;
  }

  @media (max-width: 359px) {
    padding: 0.4rem 0.4rem;
    font-size: max(13px, var(--fs-xs));
  }

  @media (min-width: 768px) {
    font-size: var(--fs-base);

    svg {
      display: block;
    }
  }
`;

const Panel = styled.div`
  padding: 20px 16px 22px;
  color: var(--text-primary);

  @media (min-width: 768px) {
    padding: 30px 34px 32px;
  }

  /* An author rule for display would otherwise beat the user-agent [hidden] rule. */
  &[hidden] {
    display: none;
  }

  &:focus-visible {
    outline: 3px solid var(--davinci-blue-fill);
    outline-offset: -3px;
  }
`;

const ServiceTabs: React.FC<ServiceTabsProps> = ({ label, tabs, onSelect }) => {
  const baseId = useId();
  const [active, setActive] = useState(0);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const select = (index: number) => {
    if (index === active) return;
    setActive(index);
    onSelect?.(tabs[index].key);
  };

  const move = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    let next: number;
    switch (event.key) {
      case 'ArrowRight':
        next = (index + 1) % tabs.length;
        break;
      case 'ArrowLeft':
        next = (index - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = tabs.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    select(next);
    tabRefs.current[next]?.focus();
  };

  return (
    <Card>
      <TabList role="tablist" aria-label={label}>
        {tabs.map((tab, index) => {
          const selected = index === active;
          return (
            <Tab
              key={tab.key}
              ref={(node) => {
                tabRefs.current[index] = node;
              }}
              type="button"
              role="tab"
              id={`${baseId}-tab-${tab.key}`}
              aria-selected={selected}
              aria-controls={`${baseId}-panel-${tab.key}`}
              tabIndex={selected ? 0 : -1}
              onClick={() => select(index)}
              onKeyDown={(event) => move(event, index)}
            >
              {tab.icon}
              {tab.label}
            </Tab>
          );
        })}
      </TabList>
      {tabs.map((tab, index) => (
        <Panel
          key={tab.key}
          role="tabpanel"
          id={`${baseId}-panel-${tab.key}`}
          aria-labelledby={`${baseId}-tab-${tab.key}`}
          tabIndex={0}
          hidden={index !== active}
        >
          {tab.panel}
        </Panel>
      ))}
    </Card>
  );
};

export default ServiceTabs;
